/**
 * NameScanTemplate.tsx — Name SocialScan.
 * Covers: combined_name, name, name_by_country.
 *
 * Your combined_name payload returned 66 profiles across 8 networks plus 4
 * Pluto personal records. The old popup rendered them as an undifferentiated
 * blob of avatars. This filters by network, paginates, and — importantly —
 * says out loud that a name search returns no match score, so these are
 * candidates rather than confirmed identities.
 *
 * Snapchat (`from: 'snapchat'`, see `extractSnapchat` below) is one more
 * such source: a candidate profile card (display name, bitmoji avatar,
 * link), a Bio/Subscribers line in Contacts, and every `storyUrls` entry
 * fed into the shared Photos tile.
 */
import * as React from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { networkMeta, NETWORK, radii } from '../tokens'
import { cleanScalar } from '../adapters/providers'
import { getLinkToProfile } from '../../../function/string'
import { useLinkPreview } from '../ui/linkPreview'
import {
  Cols, FieldTable, NoData, Photo, RawCard, ResCard, Sech, ShowMoreButton, Stats, Tri, toImageSrc, useShowMore, usePhotoLightbox, ValueList, Warn, useTokens,
} from '../ui/primitives'
import type { ValueRow } from '../ui/primitives'
import { hasMapPins, MapCard } from '../ui/MapCard'
import type { TemplateProps } from '../registry'

/**
 * Fixed height every Names/Contacts/Locations/Map tile is stretched to, via
 * ResCard's `fill` prop — the exact same constant/pattern PersonTemplate's
 * card grid uses, so a capped ValueList (5 rows, ResCard's own default)
 * fits without clipping and anything longer scrolls inside the tile
 * (`fill`'s own `overflow-y: auto`) instead of growing the row.
 */
const TILE_HEIGHT = 250

type Profile = { network: string; name: string; id: string; url?: string; image?: string }
type PersonRecord = [string, string][]
/**
 * Names / Contacts / Locations panels: PLUTO's own rows, webmii's/webmii_social's
 * `profileData`/`profile` blocks, and allServicesNames' ProfileItem rows all
 * carry structured personal fields (full name, email, phone, social-network
 * handles, address/city) under a dozen different field-name spellings per
 * provider — before this, those fields only ever surfaced as raw label/value
 * pairs buried inside "Personal records" (capped at 3 records) or the full
 * RawCard dump. `Bag` collects them across every entity into three deduped,
 * source-tagged lists so they get a dedicated, always-visible panel instead.
 */
type Bag = Map<string, { value: string; sources: Set<string> }>
type ContactBag = Map<string, { value: string; note: string; sources: Set<string> }>

function bagPush(bag: Bag, raw: any, source: string) {
  const v = cleanScalar(Array.isArray(raw) ? raw.join(', ') : raw ?? '')
  if (!v) return
  const key = v.toLowerCase()
  const existing = bag.get(key)
  if (existing) existing.sources.add(source)
  else bag.set(key, { value: v, sources: new Set([source]) })
}

function bagContact(bag: ContactBag, note: string, raw: any, source: string) {
  const v = cleanScalar(raw ?? '')
  if (!v) return
  const key = `${note}|${v.toLowerCase()}`
  const existing = bag.get(key)
  if (existing) existing.sources.add(source)
  else bag.set(key, { value: v, note, sources: new Set([source]) })
}

function toValueRows(bag: Bag): ValueRow[] {
  return Array.from(bag.values()).map((v) => ({ value: v.value, sources: Array.from(v.sources) }))
}

/**
 * Same field-name list `service.ts`'s `prepareCommonResult` already
 * recognises for `combined_name`/deepweb rows (FacebookId/linkedinId/vkId/
 * telegramId camelCase aliases, first_name+last_name, fullName, tags,
 * email/emailAddress, address/location) — duplicated here rather than
 * imported because `NameScanTemplate` reads `apiDataEntities` directly and
 * never calls `prepareCommonResult` (see that function's own comments for
 * why the two pipelines are separate). Runs on every row regardless of
 * whether it also turned out to be profile-shaped, since a `location` or
 * `email` can sit right next to `social_network`/`profileName` on the same
 * PLUTO/allServicesNames row.
 */
/**
 * Every field-name spelling `extractPersonal` recognises for a single
 * network handle, alongside the network key `networkMeta` understands and
 * the `snName` `getLinkToProfile` (function/string.ts — the same helper the
 * old UI used to turn a bare `FacebookId`/`vk_id`/... into a clickable
 * profile link) expects for that network.
 */
const NETWORK_ID_FIELDS: { pick: (item: any) => any; network: keyof typeof NETWORK; snName: string; note: string }[] = [
  { pick: (i) => i.facebook_id || i.FacebookId || i?.profile?.facebookUsername, network: 'FACEBOOK', snName: 'facebook_id', note: 'Facebook' },
  { pick: (i) => i.linkedinUsername || i.linkedin_id || i.linkedinId, network: 'LINKEDIN', snName: 'linkedin_id', note: 'LinkedIn' },
  { pick: (i) => i.vk_id || i.VkId || i.vkId, network: 'VK', snName: 'vk_id', note: 'VK' },
  { pick: (i) => i.telegram_id || i.telegramId, network: 'TELEGRAM', snName: 'telegram_id', note: 'Telegram' },
  { pick: (i) => i.twitter_username || i.twitterUsername, network: 'TWITTER', snName: 'twitter_username', note: 'Twitter' },
  { pick: (i) => i.instagramUsername || i?.profile?.instagramUsername, network: 'INSTAGRAM', snName: 'instagramUsername', note: 'Instagram' },
]

/**
 * `r.social_network`/`profile_id`/`profileName` mark a "rich" ProfileItem —
 * a candidate PLUTO/allServicesNames rows sometimes ship instead: no
 * `type: "ProfileItem"` wrapper at all, just a flat personal record
 * (first_name/last_name/phoneNumber/address) carrying a bare `FacebookId`
 * (or vk_id/linkedin_id/...) alongside it. Those used to only ever surface
 * as a "Facebook: 100019231507639" line buried in Contacts — no link, no
 * card, easy to miss, and duplicated the moment the same id also became a
 * Candidate profile elsewhere. This turns each one into its own candidate
 * card instead, with a real link built the same way the old UI's
 * getLinkToProfile did (numeric Facebook ids -> profile.php?id=, everything
 * else -> the network's normal /handle path).
 */
function extractNetworkIdProfiles(item: any, source: string): Profile[] {
  if (!item || typeof item !== 'object') return []
  const name1 = [item.first_name, item.last_name].filter(Boolean).join(' ')
  const name2 = [item.firstName, item.lastName].filter(Boolean).join(' ')
  const displayName = cleanScalar(item.fullName || item.full_name || name1 || name2 || '')
  const out: Profile[] = []
  NETWORK_ID_FIELDS.forEach(({ pick, network, snName }) => {
    const raw = pick(item)
    if (!raw) return
    const id = String(raw)
    out.push({
      network,
      name: displayName,
      id,
      url: getLinkToProfile(snName, id) || undefined,
    })
  })
  return out
}

function extractPersonal(item: any, source: string, names: Bag, contacts: ContactBag, locations: Bag, skipNetworkContacts = false) {
  if (!item || typeof item !== 'object') return

  if (!skipNetworkContacts) {
    NETWORK_ID_FIELDS.forEach(({ pick, note }) => {
      const raw = pick(item)
      if (raw) bagContact(contacts, note, raw, source)
    })
  }

  if (item.email) bagContact(contacts, 'Email', item.email, source)
  if (item.emailAddress) String(item.emailAddress).split(',').forEach((em: string) => bagContact(contacts, 'Email', em, source))
  if (item.phone) bagContact(contacts, 'Phone', item.phone, source)
  if (item.phoneNumber) bagContact(contacts, 'Phone', item.phoneNumber, source)

  const name1 = [item.first_name, item.last_name].filter(Boolean).join(' ')
  const name2 = [item.firstName, item.lastName].filter(Boolean).join(' ')
  if (name1.trim()) bagPush(names, name1, source)
  if (name2.trim()) bagPush(names, name2, source)
  if (item.fullName) bagPush(names, item.fullName, source)
  if (item.full_name) bagPush(names, item.full_name, source)
  if (item?.profile?.name) bagPush(names, item.profile.name, source)
  if (typeof item.tags === 'string') bagPush(names, item.tags, source)

  if (item.address) bagPush(locations, item.address, source)
  if (item.location) bagPush(locations, item.location, source)
}
/** A single web hit surfaced by Webmii — a Wikipedia entry, a news article,
 * a linked social profile, etc. `network` is the Webmii section name
 * ('wikipedia' | 'news' | 'web' | 'instagram' | ...). */
type Mention = { network: string; title: string; url?: string; domain?: string; text?: string; image?: string; date?: string }
type WebmiiProfile = { name?: string; score?: number; wikipediaDescription?: string }

/**
 * Webmii's `web_results`/section items carry no per-item platform field —
 * `network` here is just the Webmii section name ('web', 'wikipedia', ...),
 * which is why the old UI's "Social Networks:" card (icon + title + snippet
 * + thumbnail per Facebook/LinkedIn/etc. hit) had no equivalent here: every
 * card rendered with the same generic silhouette icon regardless of which
 * platform `result_link` actually pointed at. Same technique the app
 * already uses for Luna's `personal_profiles` domain (`normalizeSocialNetwork`
 * in function/string.ts) — a hostname match against `NETWORK`'s known keys
 * — applied here to `mention.url` so a Facebook/Instagram/LinkedIn/etc. hit
 * gets that network's real brand colour and label instead of a bare "WEB".
 */
const MENTION_HOST_NETWORK: [RegExp, keyof typeof NETWORK][] = [
  [/(^|\.)facebook\.com$/i, 'FACEBOOK'],
  [/(^|\.)instagram\.com$/i, 'INSTAGRAM'],
  [/(^|\.)linkedin\.com$/i, 'LINKEDIN'],
  [/(^|\.)(twitter\.com|x\.com)$/i, 'TWITTER'],
  [/(^|\.)vk\.com$/i, 'VK'],
  [/(^|\.)tiktok\.com$/i, 'TIKTOK'],
  [/(^|\.)ok\.ru$/i, 'OK'],
  [/(^|\.)bsky\.app$/i, 'BLUESKY'],
  [/(^|\.)skype\.com$/i, 'SKYPE'],
]

function detectMentionNetwork(url?: string): string | undefined {
  if (!url) return undefined
  let host = ''
  try {
    host = new URL(url).hostname
  } catch {
    return undefined
  }
  const hit = MENTION_HOST_NETWORK.find(([re]) => re.test(host))
  return hit?.[1]
}
/** A single photo pulled out of a webmii_social post — `extractWebmiiSocial`
 * used to only keep `images_url[0]` per post as a small mention thumbnail,
 * discarding the rest; this collects every entry in every post's
 * `images_url` array so they can all surface in a dedicated "Photos" card. */
type PhotoItem = { image: string; source: string }

/**
 * Webmii's two entities ('webmii' and 'webmii_social') don't carry
 * social_network/profile_id/profileName like every other combined_name
 * source, so the generic branch below used to swallow them whole and
 * stringify their nested objects into unreadable "[object Object]" record
 * rows (confirmed against a real webmii_social payload — its `profile` and
 * `sections` objects were the only two fields that mattered, and both got
 * flattened to junk). These two branches read their actual shape instead:
 *   - webmii_social: { profile: {name,score,wikipediaDescription,...},
 *     sections: { [sectionName]: PostItem[] } }
 *   - webmii:        { profileData: { webmii_score, contact_names,
 *     web_results: [...], facebook_id, instagram_id, linkedin_id, ... } }
 */
/**
 * webmii_social's `profile` block gives a bare handle per network
 * (`facebookUsername`/`instagramUsername`/`twitterUsername`) — no URL, no
 * profile id, unlike every other source that feeds "Candidate profiles"
 * (allServicesNames rows already carry `profile_url`). Reconstructs the
 * standard `https://<network>.com/<handle>` link so these handles show up
 * as clickable cards in that same panel instead of only surfacing as
 * unlinked text in the Contacts list.
 */
function socialProfileUrl(network: 'facebook' | 'instagram' | 'twitter', usernameRaw: string): { id: string; url: string } | null {
  const id = cleanScalar(usernameRaw).replace(/^@/, '').trim()
  if (!id) return null
  const base = network === 'facebook' ? 'https://facebook.com/' : network === 'instagram' ? 'https://instagram.com/' : 'https://twitter.com/'
  return { id, url: base + id }
}

function extractWebmiiSocial(rows: any[], mentions: Mention[], names: Bag, contacts: ContactBag, photos: PhotoItem[], profiles: Profile[]): WebmiiProfile | null {
  let profile: WebmiiProfile | null = null
  rows.forEach((r: any) => {
    if (!r || typeof r !== 'object') return
    if (r.profile && !profile) {
      profile = {
        name: r.profile.name || undefined,
        score: typeof r.profile.score === 'number' ? r.profile.score : undefined,
        wikipediaDescription: cleanScalar(r.profile.wikipediaDescription || '') || undefined,
      }
    }
    if (r.profile?.name) bagPush(names, r.profile.name, 'webmii_social')
    if (r.profile?.facebookUsername) bagContact(contacts, 'Facebook', r.profile.facebookUsername, 'webmii_social')
    if (r.profile?.instagramUsername) bagContact(contacts, 'Instagram', r.profile.instagramUsername, 'webmii_social')
    if (r.profile?.twitterUsername) bagContact(contacts, 'Twitter', r.profile.twitterUsername, 'webmii_social')
    if (r.profile?.youtubeLabel) bagContact(contacts, 'YouTube', r.profile.youtubeLabel, 'webmii_social')
    ;([
      ['facebook', 'FACEBOOK', r.profile?.facebookUsername],
      ['instagram', 'INSTAGRAM', r.profile?.instagramUsername],
      ['twitter', 'TWITTER', r.profile?.twitterUsername],
    ] as const).forEach(([network, label, raw]) => {
      if (!raw) return
      const link = socialProfileUrl(network, String(raw))
      if (link) profiles.push({ network: label, name: link.id, id: link.id, url: link.url })
    })
    const sections = r.sections && typeof r.sections === 'object' ? r.sections : {}
    Object.entries(sections).forEach(([section, items]) => {
      if (!Array.isArray(items)) return
      items.forEach((item: any) => {
        if (!item) return
        mentions.push({
          network: section,
          title: cleanScalar(item.raw?.title || item.post_url || section),
          url: item.post_url || undefined,
          domain: item.raw?.domain || undefined,
          text: cleanScalar(item.text || '') || undefined,
          image: Array.isArray(item.images_url) ? item.images_url[0] : undefined,
          date: item.created_date || undefined,
        })
        if (Array.isArray(item.images_url)) {
          item.images_url.forEach((url: any) => {
            if (typeof url === 'string' && url) photos.push({ image: url, source: 'webmii_social' })
          })
        }
      })
    })
  })
  return profile
}

function extractWebmii(rows: any[], mentions: Mention[], names: Bag, contacts: ContactBag, locations: Bag): WebmiiProfile | null {
  let profile: WebmiiProfile | null = null
  rows.forEach((r: any) => {
    if (!r || typeof r !== 'object') return
    const pd = r.profileData
    if (pd && !profile) {
      profile = {
        name: cleanScalar(r.fullName || (Array.isArray(pd.contact_names) ? pd.contact_names[0] : '') || '') || undefined,
        score: pd.webmii_score !== undefined && pd.webmii_score !== null ? Number(pd.webmii_score) : undefined,
      }
    }
    if (r.fullName) bagPush(names, r.fullName, 'webmii')
    if (Array.isArray(pd?.contact_names)) pd.contact_names.forEach((n: string) => bagPush(names, n, 'webmii'))
    if (r.profileCity) bagPush(locations, r.profileCity, 'webmii')
    if (pd?.facebook_id) bagContact(contacts, 'Facebook', pd.facebook_id, 'webmii')
    if (pd?.instagram_id) bagContact(contacts, 'Instagram', pd.instagram_id, 'webmii')
    if (pd?.linkedin_id) bagContact(contacts, 'LinkedIn', pd.linkedin_id, 'webmii')
    if (pd?.twitter_id) bagContact(contacts, 'Twitter', pd.twitter_id, 'webmii')
    if (Array.isArray(pd?.web_results)) {
      pd.web_results.forEach((w: any) => {
        if (!w) return
        mentions.push({
          network: 'web',
          title: cleanScalar(w.result_title || w.result_link || 'web'),
          url: w.result_link || undefined,
          text: cleanScalar(w.result_preview || '') || undefined,
          image: w.result_image || undefined,
        })
      })
    }
  })
  return profile
}

/**
 * Snapchat's combined_name entity (`from: 'snapchat'`) ships one row per
 * candidate, shaped nothing like PLUTO/allServicesNames:
 *   { status, coverage, data: { username, displayName, bio,
 *     subscriberCount, bitmojiAvatarUrl, storyUrls: [...], profileType,
 *     storyUrlsTruncated, storyUrlsTotal }, resolution, source: 'snapchat' }
 * There's no `social_network`/`profile_id` field for the generic branch in
 * `extract()` below to key off, and `bio`/`subscriberCount` aren't handles
 * it knows how to read either — so, same as webmii, this gets its own
 * branch instead of falling into the generic PersonRecord dump.
 *
 * `bio` and `subscriberCount` describe the Snapchat PROFILE, not a way to
 * reach the person — putting them in Contacts (alongside actual emails/
 * phones/handles) mislabelled a bio blurb and a follower count as contact
 * info, and they don't fit Names/Locations either. They go in `others`
 * instead — a catch-all panel for exactly this kind of source-specific
 * profile metadata that isn't a name, a contact method, or a location.
 */
function extractSnapchat(
  rows: any[], names: Bag, others: ContactBag, profiles: Profile[], photos: PhotoItem[], locations: Bag,
) {
  rows.forEach((r: any) => {
    const d = r?.data
    if (!d || typeof d !== 'object') return

    if (d.displayName) bagPush(names, d.displayName, 'snapchat')

    if (d.username) {
      profiles.push({
        network: 'SNAPCHAT',
        name: cleanScalar(d.displayName || d.username),
        id: String(d.username),
        url: `https://www.snapchat.com/add/${d.username}`,
        image: d.bitmojiAvatarUrl || undefined,
      })
    }

    if (d.bio) bagContact(others, 'Bio', d.bio, 'snapchat')
    if (typeof d.subscriberCount === 'number' && d.subscriberCount > 0) {
      bagContact(others, 'Subscribers', d.subscriberCount.toLocaleString(), 'snapchat')
    }
    if (d.websiteUrl) bagContact(others, 'Website', d.websiteUrl, 'snapchat')

    // Seen both as a plain string ("Los Angeles, CA") and as an object with
    // city/region/country-style keys — same two shapes handled for
    // `person.location` in service.ts's own snapchat block.
    if (typeof d.location === 'string') {
      bagPush(locations, d.location, 'snapchat')
    } else if (d.location && typeof d.location === 'object') {
      const locationText = [d.location.city, d.location.region || d.location.state, d.location.country]
        .filter(Boolean)
        .join(', ')
      if (locationText) bagPush(locations, locationText, 'snapchat')
    }

    if (Array.isArray(d.storyUrls)) {
      d.storyUrls.forEach((url: any) => {
        if (typeof url === 'string' && url) photos.push({ image: url, source: 'snapchat' })
      })
    }
  })
}

function extract(apiData: any): {
  profiles: Profile[]; records: PersonRecord[]; mentions: Mention[]; webmiiProfile: WebmiiProfile | null; photos: PhotoItem[]
  names: ValueRow[]; contacts: ValueRow[]; locations: ValueRow[]; others: ValueRow[]
} {
  const profiles: Profile[] = []
  const records: PersonRecord[] = []
  const mentions: Mention[] = []
  const photosRaw: PhotoItem[] = []
  let webmiiProfile: WebmiiProfile | null = null
  const names: Bag = new Map()
  const contacts: ContactBag = new Map()
  const locations: Bag = new Map()
  // Catch-all for source-specific profile metadata that's neither a name, a
  // contact method, nor a location — currently just Snapchat's bio/
  // subscriber count (see `extractSnapchat`), but kept generic rather than
  // named after that one source.
  const others: ContactBag = new Map()
  const entities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []

  entities.forEach((e) => {
    const from = String(e?.from || '').toLowerCase()
    const rows = Array.isArray(e?.data) ? e.data : []

    if (from === 'webmii_social') {
      webmiiProfile = extractWebmiiSocial(rows, mentions, names, contacts, photosRaw, profiles) || webmiiProfile
      return
    }
    if (from === 'webmii') {
      webmiiProfile = extractWebmii(rows, mentions, names, contacts, locations) || webmiiProfile
      return
    }
    if (from === 'snapchat') {
      extractSnapchat(rows, names, others, profiles, photosRaw, locations)
      return
    }

    rows.forEach((r: any) => {
      if (!r || typeof r !== 'object') return

      const idProfiles = (!r.social_network && !r.profile_id && !r.profileName) ? extractNetworkIdProfiles(r, from) : []

      // PLUTO / allServicesNames / any other duck-typed provider — mine the
      // known personal-field spellings on every row regardless of shape (a
      // ProfileItem row can carry `location` right alongside `social_network`).
      // Rows whose only network handle is a bare id (FacebookId, vk_id, ...)
      // have that id promoted to a Candidate profile card below instead —
      // skip re-adding it as a plain Contacts line too.
      extractPersonal(r, from, names, contacts, locations, idProfiles.length > 0)

      if (r.social_network || r.profile_id || r.profileName) {
        profiles.push({
          network: String(r.social_network || e.from || 'unknown').toUpperCase(),
          name: cleanScalar(r.profileName || r.profile_id || ''),
          id: String(r.profile_id ?? ''),
          // irbis-social-tiktok's ProfileItem rows carry the profile link as
          // a plain `url` field — `profile_url` (webmii_social/allServicesNames'
          // spelling) doesn't exist on them, so it silently produced no link
          // and CandidateProfileCard below rendered as a plain (non-clickable)
          // div instead of an `<a>`.
          url: r.profile_url || r.url || undefined,
          image: r.profileImageUrl,
        })
        if (r.profileName) bagPush(names, r.profileName, from)
      } else if (idProfiles.length > 0) {
        profiles.push(...idProfiles)
      } else {
        const fields: PersonRecord = []
        Object.entries(r).forEach(([k, v]) => {
          if (v === null || v === undefined || v === '') return
          const val = cleanScalar(v)
          if (!val) return
          fields.push([k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase()), val])
        })
        if (fields.length) records.push(fields)
      }
    })
  })
  const photos = Array.from(new Map(photosRaw.map((p) => [p.image, p])).values())

  const contactBagToRows = (bag: ContactBag): ValueRow[] =>
    bag.size ? Array.from(bag.values()).map((c) => ({ value: c.value, note: c.note, sources: Array.from(c.sources) })) : []

  return {
    profiles, records, mentions, webmiiProfile, photos,
    names: toValueRows(names), contacts: contactBagToRows(contacts),
    locations: toValueRows(locations), others: contactBagToRows(others),
  }
}

/**
 * A single Candidate profiles tile. Rows built from a bare id (see
 * `extractNetworkIdProfiles` above) carry a link but no photo or title of
 * their own — this is where that gap gets filled: `useLinkPreview` fires the
 * same live og:title/og:description/og:image proxy fetch the old UI's
 * "Social Networks:" card used, but only when there's no stored `p.image`
 * to begin with, so ProfileItem rows that already came with a real photo
 * (allServicesNames, webmii_social, ...) never trigger a fetch at all.
 */
function CandidateProfileCard({ p, index, onOpenPhoto }: { p: Profile; index: number; onOpenPhoto: (i: number) => void }) {
  const tk = useTokens()
  const meta = networkMeta(p.network)
  const needsPreview = !p.image && !!p.url
  const preview = useLinkPreview(needsPreview ? p.url : undefined)
  const loading = needsPreview && preview.status === 'loading'
  const previewImage = needsPreview ? preview.data?.images?.[0] : undefined
  // A stored `p.image` (e.g. Snapchat's bitmojiAvatarUrl) can still turn out
  // to be a dead link once the browser actually tries it — same "only
  // discoverable on load" problem `heightForRows`'s sibling fix in
  // PersonTemplate.tsx solved for its own Photos tile. Once Photo reports
  // that failure, this drops back to the plain silhouette icon instead of
  // leaving a blank tile.
  const [imgFailed, setImgFailed] = React.useState(false)
  const image = (p.image && !imgFailed) ? p.image : previewImage
  const previewTitle = !p.name && preview.data?.title ? cleanScalar(preview.data.title) : ''
  const displayName = p.name || previewTitle || p.id
  const previewDescription = preview.data?.description ? cleanScalar(preview.data.description) : ''

  return (
    <Box
      component={p.url ? 'a' : 'div'}
      href={p.url}
      target="_blank"
      rel="noreferrer"
      sx={{
        position: 'relative', display: 'flex', gap: '9px', p: '10px', textDecoration: 'none',
        border: `1px solid ${tk.border}`, borderRadius: `${radii.md}px`, background: tk.surface2,
        '&:hover': { borderColor: '#2D89F4', background: tk.brand050 },
      }}
    >
      {/* The whole card is already the link, but that's not obvious at a
       * glance — this small icon makes the "clickable, opens the profile"
       * affordance explicit instead of relying on hover-only feedback. */}
      {p.url && (
        <Box sx={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', color: tk.text3 }}>
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Box>
      )}
      <Box sx={{ position: 'relative', width: 52, height: 52, flex: 'none' }}>
        <Box
          sx={{ width: 52, height: 52, borderRadius: '9px', background: tk.surface3, overflow: 'hidden', display: 'grid', placeItems: 'center', color: tk.text3 }}
          // The card itself is a link out to the profile — this stops that
          // navigation only when there's a stored photo to enlarge, so
          // clicking it opens the lightbox instead of leaving the page. A
          // live-fetched preview image has nowhere to enlarge to (it's not
          // in the lightbox's own image list), so it stays a plain link.
          onClick={p.image && !imgFailed ? (e) => { e.preventDefault(); e.stopPropagation(); onOpenPhoto(index) } : undefined}
        >
          {loading ? (
            <CircularProgress size={16} thickness={5} sx={{ color: tk.text3 }} />
          ) : image ? (
            <Photo value={image} onLoadError={() => setImgFailed(true)} />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20" /></svg>
          )}
        </Box>
        <Box
          sx={{
            position: 'absolute', bottom: -3, right: -3, width: 18, height: 18, borderRadius: '50%',
            background: meta.color, border: `2px solid ${tk.surface2}`, display: 'grid', placeItems: 'center',
          }}
        >
          <Typography sx={{ fontSize: 8.5, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{meta.label.charAt(0)}</Typography>
        </Box>
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: '.02em' }}>
          {meta.label}
        </Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tk.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </Typography>
        {previewDescription ? (
          <Typography sx={{ fontSize: 11, color: tk.text3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {previewDescription}
          </Typography>
        ) : (
          p.id && (
            <Typography sx={{ fontSize: 11, color: tk.text3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.id}
            </Typography>
          )
        )}
      </Box>
    </Box>
  )
}

const PAGE = 18

export function NameScanTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()
  const { profiles, records, mentions, webmiiProfile, photos: photosRaw, names, contacts, locations, others } = React.useMemo(() => extract(apiData), [apiData])
  // `toImageSrc` only rejects junk/malformed values upfront — a well-formed
  // URL (Snapchat's `storyUrls`, all signed CDN links that can 404 or expire)
  // still has to be tried by the browser before you know it's dead. Same
  // brokenPhotoKeys pattern PersonTemplate.tsx uses for its own Photos tile:
  // Photo's onLoadError below adds the key once a src fails to actually
  // load, and this drops that tile instead of leaving the placeholder icon
  // showing through a blank image.
  const [brokenPhotoKeys, setBrokenPhotoKeys] = React.useState<Set<string>>(new Set())
  const photos = React.useMemo(
    () => photosRaw.filter((p) => toImageSrc(p.image) && !brokenPhotoKeys.has(p.image)),
    [photosRaw, brokenPhotoKeys],
  )
  const [net, setNet] = React.useState<string>('ALL')
  const [shown, setShown] = React.useState(PAGE)
  const [q, setQ] = React.useState('')
  const { visible: visibleMentions, open: mentionsOpen, setOpen: setMentionsOpen, hiddenCount: hiddenMentions } = useShowMore(mentions, 9)

  const counts = React.useMemo(() => {
    const c: Record<string, number> = {}
    profiles.forEach((p) => { c[p.network] = (c[p.network] || 0) + 1 })
    return c
  }, [profiles])

  const filtered = React.useMemo(
    () =>
      profiles.filter(
        (p) =>
          (net === 'ALL' || p.network === net) &&
          (!q || p.name.toLowerCase().includes(q.toLowerCase()) || p.id.toLowerCase().includes(q.toLowerCase())),
      ),
    [profiles, net, q],
  )
  const visible = filtered.slice(0, shown)
  const { openAt, lightbox } = usePhotoLightbox(visible.map((p) => p.image))
  const { openAt: openPhotoAt, lightbox: photoLightbox } = usePhotoLightbox(photos.map((p) => p.image))

  if (!profiles.length && !records.length && !mentions.length && !webmiiProfile && !names.length && !contacts.length && !locations.length && !others.length) {
    return <NoData title="No profiles matched this name" hint="Try a full name, or narrow the search with a country." />
  }

  return (
    <>
      <ResCard title="Key facts">
        <Stats
          items={[
            { label: 'Profiles', value: profiles.length, sub: `${Object.keys(counts).length} networks`, tone: 'hl' },
            { label: 'Personal records', value: records.length, sub: 'from leak data' },
            { label: 'Networks', value: Object.keys(counts).length },
            webmiiProfile?.score !== undefined
              ? { label: 'Webmii score', value: webmiiProfile.score as number, sub: `${mentions.length} web mentions`, tone: 'hl' }
              : { label: 'Confirmed', value: 0, sub: 'no match score returned', tone: 'warn' },
          ]}
        />
      </ResCard>

      {/*
        Names/Contacts/Locations/Map/Photos/Others — same standard tile
        PersonTemplate's card grid uses: a fixed `TILE_HEIGHT`, max 3 per
        row via `Cols` (auto-flow wraps extra cards, e.g. Map/Photos, onto
        their own row), and `fill` on ResCard (see primitives.tsx) which
        already wraps its content in its own `overflow-y: auto` box. Giving
        the wrapper an explicit pixel height (not `100%`, which just tracks
        whatever the tallest sibling's natural content wants) is what
        actually makes that scroll trigger instead of the row growing to
        fit every row a card happens to have — exactly how PersonTemplate's
        own Locations+Map tile is sized, so Map/Photos get the same
        fixed-height tile here rather than an unconstrained full-width
        block. Country-level highlighting only (`mapPoints={[]}`) — PLUTO/
        webmii/allServicesNames give city/country text, not lat/lng. Photos
        is placed in this same grid (not a standalone full-width ResCard
        like "Key facts") so it matches how the rest of this template lays
        out its panels; webmii_social's full `images_url` array per post
        used to be discarded down to a single inline thumbnail on the
        mention — this collects every image into its own scrollable tile.
        Others is the catch-all landing spot for source-specific profile
        metadata that isn't a name/contact/location — right now that's just
        Snapchat's bio and subscriber count (see `extractSnapchat`), kept
        out of Contacts since neither is a way to reach the person.
      */}
      {(names.length > 0 || contacts.length > 0 || locations.length > 0 || photos.length > 0 || others.length > 0) && (
        <Cols n={3}>
          {photos.length > 0 && (
            <Box sx={{ height: TILE_HEIGHT }}>
              <ResCard title="Photos" count={photos.length} fill>
                <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {photos.map((p, i) => (
                    <Box
                      key={p.image + i}
                      sx={{
                        width: 82, height: 82, borderRadius: `${radii.md}px`, overflow: 'hidden', position: 'relative',
                        background: tk.surface3, border: `1px solid ${tk.border}`, flex: 'none',
                      }}
                    >
                      <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: tk.text3 }}>
                        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <circle cx="12" cy="9" r="3.4" /><path d="M4.5 20c1-3.6 4-5.4 7.5-5.4S18.5 16.4 19.5 20" />
                        </svg>
                      </Box>
                      <Photo
                        value={p.image}
                        sx={{ position: 'relative' }}
                        onClick={() => openPhotoAt(i)}
                        onLoadError={() => setBrokenPhotoKeys((prev) => new Set(prev).add(p.image))}
                      />
                    </Box>
                  ))}
                </Box>
              </ResCard>
            </Box>
          )}
          {names.length > 0 && (
            <Box sx={{ height: TILE_HEIGHT }}>
              <ResCard title="Names" count={names.length} fill>
                <ValueList rows={names} />
              </ResCard>
            </Box>
          )}
          {contacts.length > 0 && (
            <Box sx={{ height: TILE_HEIGHT }}>
              <ResCard title="Contacts" count={contacts.length} fill>
                <ValueList rows={contacts} />
              </ResCard>
            </Box>
          )}
          {locations.length > 0 && (
            <Box sx={{ height: TILE_HEIGHT }}>
              <ResCard title="Locations" count={locations.length} fill>
                <ValueList rows={locations} />
              </ResCard>
            </Box>
          )}
          {hasMapPins([], locations) && (
            <Box sx={{ height: TILE_HEIGHT }}>
              <MapCard addressCount={locations.length} mapPoints={[]} addresses={locations} />
            </Box>
          )}
          {others.length > 0 && (
            <Box sx={{ height: TILE_HEIGHT }}>
              <ResCard title="Others" count={others.length} fill>
                <ValueList rows={others} />
              </ResCard>
            </Box>
          )}
        </Cols>
      )}

      <ResCard
        title="Candidate profiles"
        count={filtered.length}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', background: tk.surface2, border: `1px solid ${tk.border}`, borderRadius: '9px', px: '10px', py: '6px', maxWidth: 230 }}>
            <Box component="span" sx={{ color: tk.text3, display: 'flex' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></svg>
            </Box>
            <Box component="input" value={q} onChange={(e: any) => { setQ(e.target.value); setShown(PAGE) }} placeholder="Filter by handle or id"
              sx={{ border: 0, background: 'transparent', outline: 0, color: tk.text, fontSize: 12.5, width: '100%', fontFamily: 'inherit' }} />
          </Box>
        }
      >
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mb: '11px' }}>
          <NetBtn on={net === 'ALL'} onClick={() => { setNet('ALL'); setShown(PAGE) }} label="All" count={profiles.length} />
          {Object.entries(counts).map(([k, v]) => (
            <NetBtn key={k} on={net === k} onClick={() => { setNet(k); setShown(PAGE) }} label={networkMeta(k).label} count={v} colour={networkMeta(k).color} />
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '9px' }}>
          {visible.map((p, i) => (
            <CandidateProfileCard key={i} p={p} index={i} onOpenPhoto={openAt} />
          ))}
        </Box>

        {filtered.length > shown && (
          <Box sx={{ textAlign: 'center', mt: '12px' }}>
            <Box component="button" onClick={() => setShown((s) => s + 24)}
              sx={{ border: `1px solid ${tk.border}`, background: 'none', cursor: 'pointer', borderRadius: '8px', px: '12px', py: '6px', fontSize: 11.5, fontWeight: 600, color: tk.text2 }}>
              Show {Math.min(24, filtered.length - shown)} more of {filtered.length}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: '12px' }}>
          <Warn title="These are name matches, not confirmed identities">
            The API returns no match score for name search. Confirm a candidate with Face WebScan or an E-mail Lookup before acting on it.
          </Warn>
        </Box>
      </ResCard>

      {(mentions.length > 0 || webmiiProfile?.wikipediaDescription) && (
        <ResCard title="Profiles" count={mentions.length}>
          {webmiiProfile?.wikipediaDescription && (
            <Box sx={{ mb: '12px', p: '10px 12px', borderRadius: `${radii.md}px`, background: tk.surface2, border: `1px solid ${tk.border}` }}>
              <Typography sx={{ fontSize: 12.5, color: tk.text2, lineHeight: 1.5 }}>
                {webmiiProfile.wikipediaDescription}
              </Typography>
            </Box>
          )}
          {mentions.length > 0 && (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '9px' }}>
                {visibleMentions.map((m, i) => {
                  const net = detectMentionNetwork(m.url)
                  const meta = net ? networkMeta(net) : null
                  return (
                    <Box
                      key={i}
                      component={m.url ? 'a' : 'div'}
                      href={m.url}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        display: 'flex', gap: '9px', p: '10px', textDecoration: 'none',
                        border: `1px solid ${tk.border}`, borderRadius: `${radii.md}px`, background: tk.surface2,
                        '&:hover': { borderColor: '#2D89F4', background: tk.brand050 },
                      }}
                    >
                      <Box sx={{ position: 'relative', width: 52, height: 52, flex: 'none' }}>
                        <Box sx={{ width: 52, height: 52, borderRadius: '9px', background: tk.surface3, overflow: 'hidden', display: 'grid', placeItems: 'center', color: tk.text3 }}>
                          {m.image ? (
                            <Photo value={m.image} />
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20" /></svg>
                          )}
                        </Box>
                        {meta && (
                          <Box
                            sx={{
                              position: 'absolute', bottom: -3, right: -3, width: 18, height: 18, borderRadius: '50%',
                              background: meta.color, border: `2px solid ${tk.surface2}`, display: 'grid', placeItems: 'center',
                            }}
                          >
                            <Typography sx={{ fontSize: 8.5, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{meta.label.charAt(0)}</Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: meta?.color || '#7C5CFF', textTransform: 'uppercase', letterSpacing: '.02em' }}>
                          {meta?.label || m.network}{m.domain ? ` · ${m.domain}` : ''}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tk.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {m.title}
                        </Typography>
                        {m.text && (
                          <Typography sx={{ fontSize: 11, color: tk.text3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {m.text}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
              <ShowMoreButton open={mentionsOpen} hiddenCount={hiddenMentions} onClick={() => setMentionsOpen((s) => !s)} />
            </>
          )}
        </ResCard>
      )}

      {records.length > 0 && (
        <ResCard title="Personal records" count={records.length}>
          <Tri>
            {records.slice(0, 3).map((r, i) => (
              <div key={i}>
                <Sech>Record {i + 1}</Sech>
                <FieldTable rows={r} />
              </div>
            ))}
          </Tri>
        </ResCard>
      )}

      <RawCard data={apiData} />
      {lightbox}
      {photoLightbox}
    </>
  )
}

function NetBtn({ on, onClick, label, count, colour }: { on: boolean; onClick: () => void; label: string; count: number; colour?: string }) {
  const tk = useTokens()
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: '6px', px: '10px', py: '5px', borderRadius: '8px', cursor: 'pointer',
        border: `1px solid ${on ? '#2D89F4' : tk.border}`, background: on ? '#2D89F4' : tk.surface2,
        color: on ? '#fff' : tk.text2, fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit',
      }}
    >
      {colour && <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: on ? '#fff' : colour }} />}
      {label} <b>{count}</b>
    </Box>
  )
}
